import { useState, useCallback, useMemo } from 'react';

import { Navigate, useParams, useNavigate } from 'react-router-dom';

import { useButtonPressedCallback, useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import ErrorState from '@zenbusiness/zui/ErrorState';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import PageContainer from '@zenbusiness/zui/PageContainer';
import PageLoading from '@zenbusiness/zui/PageLoading';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { CONTACT_US_URL } from '#client/constants';
import { ENABLE_DASHBOARD_SUBSCRIPTION_DNR } from '#client/constants/experimentsFlags';
import { billingSubscriptions } from '#client/constants/routes';

import {
  SubscriptionCancelActions,
  SubscriptionCancelConfirmation,
  SubscriptionCancelHeader,
  SubscriptionCancelHelp,
  SubscriptionBenefits,
  SubscriptionCancelReason,
  SubscriptionCancelResult,
  SubscriptionSummaryContent,
} from './components';
import { StyledSubscriptionCard, StyledContinueButtonRow } from './SubscriptionCancelPage.style';
import { CANCEL_FLOW_TYPES, buildSubscriptionContext, getCancelClickstreamProps } from './utils/cancelFlowConfig';

import { useBillingSubscriptionChange } from '../../hooks/useBillingSubscriptionChange';
import { useBillingSubscriptions } from '../../hooks/useBillingSubscriptions';
import { SQSP_DOMAIN_TIER_SERVICE_PREFIX } from '../Subscriptions/constants';
import { isCustomerActionAllowed } from '../Subscriptions/utils/selfServiceDnrEligibility';

const STEP_CONFIRM = 1;
const STEP_REASON = 2;
const STEP_SUBMIT = 3;
const STEP_LOADING = 4;
const STEP_RESULT = 5;

function getCancelSummaryDate(cancelFlowType, subscription) {
  if (cancelFlowType === CANCEL_FLOW_TYPES.SUBSCRIPTION) {
    // Paid cancel copy is framed around the active-through date for the current term.
    // If billing does not provide that boundary, do not show this self-service route.
    return subscription?.termEndDate;
  }

  return subscription?.termStartDate;
}

export default function SubscriptionCancelPage() {
  const { businessEntityUuid, orderBillingItemUuid } = useParams();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const [step, setStep] = useState(STEP_CONFIRM);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [changeReasonDisplay, setChangeReasonDisplay] = useState('');
  const [changeDescription, setChangeDescription] = useState('');

  const subscriptionDnrEnabled = useExperimentFlag(ENABLE_DASHBOARD_SUBSCRIPTION_DNR, null);
  const { data: subscriptions, loading, hasError } = useBillingSubscriptions();
  const { cancelSubscription } = useBillingSubscriptionChange();

  const subscription = useMemo(
    () => subscriptions.find((s) => s.orderBillingItemUuid === orderBillingItemUuid),
    [subscriptions, orderBillingItemUuid],
  );
  const showRenewalHeading = subscription?.service?.startsWith(SQSP_DOMAIN_TIER_SERVICE_PREFIX);
  let cancelFlowType = null;
  if (subscriptionDnrEnabled === true && isCustomerActionAllowed(subscription, 'doNotRenew')) {
    cancelFlowType = CANCEL_FLOW_TYPES.SUBSCRIPTION;
  } else if (isCustomerActionAllowed(subscription, 'cancelTrial')) {
    cancelFlowType = CANCEL_FLOW_TYPES.TRIAL;
  }
  // Hooks are initialized before the route guard resolves. This fallback is only
  // used during loading/redirect renders where no cancel action is available.
  const trackedCancelFlowType = cancelFlowType || CANCEL_FLOW_TYPES.TRIAL;
  const subscriptionDnrFlagLoading = subscriptionDnrEnabled === null;

  const dashboardPath = `/dashboard/${businessEntityUuid}`;
  const subscriptionsPath = billingSubscriptions;

  const subscriptionContext = buildSubscriptionContext(subscription);

  const trackCancelSuccess = useButtonPressedCallback(
    getCancelClickstreamProps(trackedCancelFlowType, 'success', subscriptionContext),
  );
  const trackCancelError = useClickstreamCallback(
    'errorDisplayed',
    getCancelClickstreamProps(trackedCancelFlowType, 'error', subscriptionContext),
  );
  const handleBack = useCallback(() => {
    if (step === STEP_LOADING || step === STEP_RESULT) {
      return;
    }
    if (step > STEP_CONFIRM) {
      setStep((s) => s - 1);
    } else {
      navigate(-1);
    }
  }, [step, navigate]);

  const handleClose = useCallback(() => {
    navigate(subscriptionsPath);
  }, [navigate, subscriptionsPath]);

  const handleConfirmContinue = useCallback(() => {
    setStep(STEP_REASON);
  }, []);

  const handleReasonContinue = useCallback(() => {
    setStep(STEP_SUBMIT);
  }, []);

  const handleSubmit = useCallback(async () => {
    setStep(STEP_LOADING);

    try {
      const result = await cancelSubscription({
        orderBillingItemUuid,
        changeType: 'cancel_renewal',
        changeReason,
        changeDescription: changeDescription || undefined,
      });

      const statusCode = result?.data?.billingSubscriptionChange?.statusCode;
      const success = statusCode ? statusCode >= 200 && statusCode < 300 : false;
      if (statusCode === undefined) {
        console.warn('SubscriptionCancelPage: billingSubscriptionChange response missing statusCode', result);
      }
      if (success) {
        trackCancelSuccess();
      } else {
        trackCancelError();
      }
      setSubmitSuccess(success);
      setStep(STEP_RESULT);
    } catch {
      trackCancelError();
      setSubmitSuccess(false);
      setStep(STEP_RESULT);
    }
  }, [cancelSubscription, orderBillingItemUuid, changeReason, changeDescription, trackCancelSuccess, trackCancelError]);

  const handleReturnToSubscriptions = useCallback(() => {
    navigate(subscriptionsPath);
  }, [navigate, subscriptionsPath]);

  const handleReturnToDashboard = useCallback(() => {
    navigate(dashboardPath);
  }, [navigate, dashboardPath]);

  const handleContactUs = useCallback(() => {
    window.location.href = CONTACT_US_URL;
  }, []);

  if (loading) {
    return <PageLoading />;
  }

  if (hasError) {
    return (
      <PageContainer>
        <ErrorState
          title={formatMessage({ id: 'dashboard.subscriptionCancel.error.loading.title' })}
          message={formatMessage({ id: 'dashboard.subscriptionCancel.error.loading.message' })}
        />
      </PageContainer>
    );
  }

  if (!subscription) {
    return <Navigate to={subscriptionsPath} replace />;
  }

  if (!subscription.inTrial && subscriptionDnrFlagLoading) {
    return <PageLoading />;
  }

  const cancelSummaryDate = getCancelSummaryDate(cancelFlowType, subscription);
  const paidCancelMissingSummaryDate = cancelFlowType === CANCEL_FLOW_TYPES.SUBSCRIPTION && !cancelSummaryDate;

  if (!cancelFlowType || paidCancelMissingSummaryDate) {
    return <Navigate to={subscriptionsPath} replace />;
  }

  const canContinueReason = changeReason && changeDescription.length <= 255;
  const subscriptionWithCancelSummary = {
    ...subscription,
    summaryDate: cancelSummaryDate,
  };

  return (
    <Box bgcolor="background.default" minHeight="100vh">
      <SubscriptionCancelHeader
        cancelFlowType={cancelFlowType}
        onBack={step !== STEP_CONFIRM && step !== STEP_RESULT ? handleBack : undefined}
        onClose={handleClose}
        subscriptionContext={subscriptionContext}
      />

      <Stack gap={3} maxWidth={600} mx="auto" py={5} px={3}>
        {step === STEP_CONFIRM && (
          <>
            <Text size="2xl" weight="bold" family="secondary">
              <FormattedMessage
                id={`dashboard.subscriptionCancel.confirm.heading${showRenewalHeading ? '.renewal' : ''}`}
              />
            </Text>

            <StyledSubscriptionCard>
              <SubscriptionSummaryContent
                period={subscription.period}
                summary={subscription.summary}
                service={subscription.service}
                titleMessageId={subscription.titleMessageId}
                price={subscription.price}
                summaryDate={subscriptionWithCancelSummary.summaryDate}
                inTrial={subscription.inTrial}
                trialDays={subscription.trialDays}
                cancelFlowType={cancelFlowType}
              />
            </StyledSubscriptionCard>

            <SubscriptionBenefits
              benefits={subscription.benefits}
              benefitMessageIds={subscription.benefitMessageIds}
              service={subscription.service}
            />

            <SubscriptionCancelHelp cancelFlowType={cancelFlowType} />

            <SubscriptionCancelActions
              cancelFlowType={cancelFlowType}
              onConfirm={handleConfirmContinue}
              subscriptionContext={subscriptionContext}
            />
          </>
        )}

        {step === STEP_REASON && (
          <>
            <SubscriptionCancelReason
              cancelFlowType={cancelFlowType}
              changeReason={changeReason}
              changeDescription={changeDescription}
              onChangeReason={setChangeReason}
              onChangeReasonDisplay={setChangeReasonDisplay}
              onChangeDescription={setChangeDescription}
              subscriptionContext={subscriptionContext}
            />

            <StyledContinueButtonRow>
              <Button
                variant="contained"
                size="large"
                disabled={!canContinueReason}
                clickstreamProps={getCancelClickstreamProps(cancelFlowType, 'reasonContinue', subscriptionContext)}
                onClick={handleReasonContinue}
              >
                <FormattedMessage id="dashboard.subscriptionCancel.reason.continue" />
              </Button>
            </StyledContinueButtonRow>
          </>
        )}

        {step === STEP_SUBMIT && (
          <SubscriptionCancelConfirmation
            cancelFlowType={cancelFlowType}
            subscription={subscriptionWithCancelSummary}
            subscriptionContext={subscriptionContext}
            changeReasonDisplay={changeReasonDisplay}
            onChangeReason={() => setStep(STEP_REASON)}
            onSubmit={handleSubmit}
          />
        )}

        {step === STEP_LOADING && (
          <Stack alignItems="center" pt={6}>
            <LoadingSpinner
              title={formatMessage({ id: 'dashboard.subscriptionCancel.loading.title' })}
              subtitle={formatMessage({ id: 'dashboard.subscriptionCancel.loading.subtitle' })}
            />
          </Stack>
        )}

        {step === STEP_RESULT && (
          <SubscriptionCancelResult
            cancelFlowType={cancelFlowType}
            success={submitSuccess}
            onReturnToSubscriptions={handleReturnToSubscriptions}
            onReturnToDashboard={handleReturnToDashboard}
            onContactUs={handleContactUs}
            subscriptionContext={subscriptionContext}
          />
        )}
      </Stack>
    </Box>
  );
}
